import { createSelector, OutputParametricSelector } from "reselect";
import { MapStateToPropsFactory } from "react-redux";

import { ApplicationState, createGetThrottleSelector, Entities } from "@ppb/tbd-store";
import URN from "@ppb/tbd-store/state/layout/URN";
import { createGetGridCardSportsbookMarketsSelector } from "@ppb/tbd-store/state/layout/cards/grid/grid-cards-selectors";
import { createCardByURNSelector } from "@ppb/tbd-store/state/layout/cards/cards-selectors";
import { GridCard, GridCardLayout, GridCards, MarketBlurbInfo } from "@ppb/tbd-store/state/layout/cards/Card.types";
import { NavigationIntent } from "@ppb/tbd-store/clients/catalogue/catalogue-response-types";
import {
  AzSwitchClickAction,
  PromoDescriptionToggleAction,
  ToggleShowMoreRunnersAction,
  UI__AZ_SWITCH_CLICK,
  UI__PROMO_DESCRIPTION_TOGGLE,
  UI__TOGGLE_SHOW_MORE_RUNNERS,
} from "@ppb/tbd-store/actions/interface";
import { MarketBlurbLinkClick, UI__MARKET_BLURB_LINK_CLICK } from "@ppb/tbd-store/actions/navigation";
import {
  StartRefreshCardAction,
  START_REFRESH_CARD,
  StopRefreshCardAction,
  STOP_REFRESH_CARD,
} from "@ppb/tbd-store/actions/refresh";
import { EXTERNAL_PUSH_BLANK, ExternalPushBlankAction } from "@ppb/tbd-store/actions/router";

import { MarketBlurb, MARKET_BLURB_SUPER_SUB } from "../../config/market-blurb";
import MarketBlurbsGA4Variants from "../../helpers/market-blurbs-ga4-variants";
import {
  extractFootballPlayerRunnerContext,
  getPlayerStatValue,
  getRunnerJersey,
  STAT_LABEL_I18N_KEY,
} from "../FootballRunner/helpers/FootballRunnerHelpers";

export type ContainerProps = {
  urn: URN;
  visible?: boolean;
  // Set when nested inside another component's titled Card (e.g. PebbleCardGroup).
  outerCardRef?: HTMLDivElement | null;
  // Set by PebbleCardGroup: the group shows the super sub icon instead, so the card drops its blurb.
  isSuperSubExperimentActive?: boolean;
};

export type GridCardItem = {
  label?: string;
  marketUrn: URN;
  selectionId?: number;
};

export type GridCardLine = {
  label: string;
  items: GridCardItem[];
  jersey?: string;
  useFallbackJersey?: boolean;
  statValue?: string;
  statValueInterpolation?: Record<string, string | number>;
  statLabel?: string;
};

export type CardProps = {
  marketUrn?: URN;
  layout: GridCardLayout;
  numberOfItemsToDisplay: number;
  lines: GridCardLine[];
  marketBlurb?: MarketBlurb;
  infoBlurb?: MarketBlurbInfo;
};

export type StateProps = CardProps | Record<string, never>;

type GridCardEntities = Pick<Entities, "sportsbookmarkets" | "footballplayerseasonstats" | "throttles">;

export const createViewModel = (): OutputParametricSelector<
  ApplicationState,
  URN,
  CardProps | undefined,
  (
    card: GridCard,
    sportsbookMarkets: GridCardEntities["sportsbookmarkets"],
    footballPlayerSeasonStats: GridCardEntities["footballplayerseasonstats"],
    throttles: GridCardEntities["throttles"],
    isSuperSubExperimentActive: boolean,
  ) => CardProps | undefined
> => {
  const getGridCardByURN = createCardByURNSelector<GridCards, URN>();
  const getGridCardSportsbookMarkets = createGetGridCardSportsbookMarketsSelector();
  const getThrottle = createGetThrottleSelector();

  return createSelector(
    [
      (state: ApplicationState, urn: URN): GridCard | null => getGridCardByURN(state.layouts.cards.grids, urn),
      (state: ApplicationState): GridCardEntities["sportsbookmarkets"] => state.entities.sportsbookmarkets,
      (state: ApplicationState): GridCardEntities["footballplayerseasonstats"] =>
        state.entities.footballplayerseasonstats,
      (state: ApplicationState): GridCardEntities["throttles"] => state.entities.throttles,
      // Third argument of the selector: without it as an input, the memoized result would not follow the experiment.
      (_state: ApplicationState, _urn: URN, isSuperSubExperimentActive?: boolean): boolean =>
        !!isSuperSubExperimentActive,
    ],
    (
      card: GridCard | null,
      sportsbookMarkets: GridCardEntities["sportsbookmarkets"],
      footballPlayerSeasonStats: GridCardEntities["footballplayerseasonstats"],
      throttles: GridCardEntities["throttles"],
      isSuperSubExperimentActive: boolean,
    ) => {
      if (!card) {
        return undefined;
      }
      const isSuperSubBlurbThrottleActive = !!getThrottle(throttles, "SUPER_SUB_MARKET_BLURBS")?.isActive;
      let lines = [];

      try {
        if (card.layout === "HORIZONTAL_MARKETS") {
          lines = card.markets.map((market) => {
            const titles = [...new Set(card.runners.map((item) => item.name))];
            if (!market.displayLabel) {
              throw new Error("Missing required horizontal market displayLabel.");
            }

            return {
              label: market.displayLabel,
              items: titles.map((title) => {
                // Find a runner that has the same title and it is from same market
                const selectionId = card.runners.find((item) => market.urn === item.marketURN && item.name === title)
                  ?.selectionId;

                return {
                  label: title,
                  marketUrn: market.urn,
                  selectionId,
                };
              }),
            };
          });
        } else {
          const { jerseys, playerHomeAwayMap, playerStats } = extractFootballPlayerRunnerContext(
            getThrottle,
            throttles,
            card,
            footballPlayerSeasonStats,
          );

          const statLabel = playerStats && card.stat ? STAT_LABEL_I18N_KEY[card.stat] : undefined;
          lines = card.runners.map((runner) => {
            const { jerseyUrl, useFallbackJersey } = getRunnerJersey(jerseys, playerHomeAwayMap, runner.participantId);

            let playerStatValue: string | undefined;
            let playerStatValueInterpolation: Record<string, string | number> | undefined;

            if (playerStats && runner.participantId) {
              const statResult = getPlayerStatValue(playerStats[runner.participantId], card.stat);
              playerStatValue = statResult?.value;
              playerStatValueInterpolation = statResult?.interpolation;
            }

            return {
              label: runner.name,
              jersey: jerseyUrl,
              useFallbackJersey,
              statValue: playerStatValue,
              statValueInterpolation: playerStatValueInterpolation,
              statLabel,
              items: card.markets.map((market) => ({
                label: market.displayLabel,
                marketUrn: market.urn,
                selectionId: runner.selectionId,
              })),
            };
          });
        }
      } catch {
        return undefined;
      }

      if (!card.markets.length) {
        return undefined;
      }

      const cardMarkets = getGridCardSportsbookMarkets(card, sportsbookMarkets);
      const hasSuperSubBlurb =
        isSuperSubBlurbThrottleActive &&
        !isSuperSubExperimentActive &&
        !!cardMarkets?.some(({ isSuperSub }): boolean => isSuperSub);

      return {
        marketUrn: card.markets[0]?.urn,
        marketBlurb: hasSuperSubBlurb ? MARKET_BLURB_SUPER_SUB : undefined,
        infoBlurb: card.infoBlurbs?.[0],
        layout: card.layout,
        numberOfItemsToDisplay: card.numberOfItemsToDisplay || lines.length,
        lines,
      };
    },
  );
};

export const makeMapStateToProps: MapStateToPropsFactory<StateProps, ContainerProps, ApplicationState> = () => {
  const getViewModel = createViewModel();

  return function mapStateToProps(
    state: ApplicationState,
    { urn, isSuperSubExperimentActive }: ContainerProps,
  ): StateProps {
    const viewModel = getViewModel(state, urn, isSuperSubExperimentActive);

    if (!viewModel) {
      return {};
    }

    return viewModel;
  };
};

const dispatchToggleShowMoreRunners = (
  cardUrn: URN,
  showMore: boolean,
  gaModuleSuffix?: string,
): ToggleShowMoreRunnersAction => ({
  type: UI__TOGGLE_SHOW_MORE_RUNNERS,
  payload: {
    cardUrn,
    showMore,
    gaModuleSuffix,
  },
});

const dispatchRefreshCard = (urn: URN, isIntersecting: boolean): StartRefreshCardAction | StopRefreshCardAction => ({
  type: isIntersecting ? START_REFRESH_CARD : STOP_REFRESH_CARD,
  payload: urn,
});

const dispatchAzSwitchClick = (label: string, isToggleOn: boolean): AzSwitchClickAction => ({
  type: UI__AZ_SWITCH_CLICK,
  payload: {
    label,
    isToggleOn,
  },
});

const dispatchTogglePromoDescription = (
  title: string,
  isOpen: boolean,
  variant: string,
): PromoDescriptionToggleAction => ({
  type: UI__PROMO_DESCRIPTION_TOGGLE,
  payload: {
    title,
    isOpen,
    variant,
  },
});

const dispatchMarketBlurbLinkClick = (destinationUrl: string, elementText: string): MarketBlurbLinkClick => ({
  type: UI__MARKET_BLURB_LINK_CLICK,
  payload: {
    destinationUrl,
    elementText,
    variant: MarketBlurbsGA4Variants.INFORMATIVE,
  },
});

const dispatchPushExternalBlankAction = (url: string): ExternalPushBlankAction => ({
  type: EXTERNAL_PUSH_BLANK,
  payload: {
    viewUrl: url,
    viewUrn: "",
    navigationIntent: NavigationIntent.External,
  },
});

export type DispatchProps = {
  dispatchToggleShowMoreRunners: typeof dispatchToggleShowMoreRunners;
  dispatchRefreshCard: typeof dispatchRefreshCard;
  dispatchAzSwitchClick: typeof dispatchAzSwitchClick;
  dispatchTogglePromoDescription: typeof dispatchTogglePromoDescription;
  dispatchMarketBlurbLinkClick: typeof dispatchMarketBlurbLinkClick;
  dispatchPushExternalBlankAction: typeof dispatchPushExternalBlankAction;
};

export const mapDispatchToProps: DispatchProps = {
  dispatchToggleShowMoreRunners,
  dispatchRefreshCard,
  dispatchAzSwitchClick,
  dispatchTogglePromoDescription,
  dispatchMarketBlurbLinkClick,
  dispatchPushExternalBlankAction,
};
